import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CostsSubsidiesSection = () => {
  const costData = [
    {
      type: 'Hybride warmtepomp',
      investment: '€4.000 - €7.000',
      subsidy: '€1.500 - €2.100',
      netCost: '€2.500 - €4.900',
      savings: '€600 - €900/jaar',
      payback: '4-7 jaar',
      icon: 'Layers'
    },
    {
      type: 'All-electric lucht/water',
      investment: '€10.000 - €18.000',
      subsidy: '€2.100 - €3.000',
      netCost: '€7.900 - €15.000',
      savings: '€1.200 - €1.800/jaar',
      payback: '7-12 jaar',
      icon: 'Zap'
    },
    {
      type: 'Bodemgebonden',
      investment: '€15.000 - €25.000',
      subsidy: '€3.000 - €4.500',
      netCost: '€12.000 - €20.500',
      savings: '€1.500 - €2.200/jaar',
      payback: '8-14 jaar',
      icon: 'Layers'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Kosten & subsidies
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Overzicht van investeringen, beschikbare subsidies en terugverdientijd.
          </p>
        </motion.div>

        {/* Cost Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-card border border-border rounded-xl shadow-elevation-2 overflow-hidden mb-12"
        >
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary text-primary-foreground">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Type warmtepomp</th>
                  <th className="px-6 py-4 text-left font-semibold">Investering</th>
                  <th className="px-6 py-4 text-left font-semibold">Subsidie (ISDE)</th>
                  <th className="px-6 py-4 text-left font-semibold">Netto kosten</th>
                  <th className="px-6 py-4 text-left font-semibold">Besparing/jaar</th>
                  <th className="px-6 py-4 text-left font-semibold">Terugverdientijd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {costData?.map((item, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="hover:bg-muted/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name={item?.icon} size={20} className="text-primary" />
                        </div>
                        <span className="font-semibold text-foreground">{item?.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{item?.investment}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {item?.subsidy}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-foreground">{item?.netCost}</td>
                    <td className="px-6 py-4 font-semibold text-success">{item?.savings}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {item?.payback}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden divide-y divide-border">
            {costData?.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="p-6 space-y-3"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name={item?.icon} size={24} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{item?.type}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground mb-1">Investering</p>
                    <p className="font-semibold text-foreground">{item?.investment}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Subsidie</p>
                    <p className="font-semibold text-green-600">{item?.subsidy}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Netto kosten</p>
                    <p className="font-semibold text-foreground">{item?.netCost}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Besparing/jaar</p>
                    <p className="font-semibold text-success">{item?.savings}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground mb-1">Terugverdientijd</p>
                    <p className="font-semibold text-blue-600">{item?.payback}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Subsidy Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {/* ISDE Subsidy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 lg:p-8"
          >
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Gift" size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">ISDE Subsidie 2026</h3>
                <p className="text-muted-foreground">Investeringssubsidie Duurzame Energie</p>
              </div>
            </div>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">Hybride: €1.500 - €2.100</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">All-electric: €2.100 - €3.000</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">Bodemgebonden: tot €4.500</span>
              </li>
            </ul>
            <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right" fullWidth>
              Meer info ISDE
            </Button>
          </motion.div>

          {/* Additional Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 lg:p-8"
          >
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="TrendingUp" size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">Extra voordelen</h3>
                <p className="text-muted-foreground">Meer dan alleen subsidie</p>
              </div>
            </div>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">Verlaagd BTW-tarief (9% i.p.v. 21%)</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">Gemeentelijke subsidies mogelijk</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">Hogere woningwaarde</span>
              </li>
            </ul>
            <Button variant="outline" size="sm" iconName="Calculator" iconPosition="right" fullWidth>
              Bereken uw voordeel
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CostsSubsidiesSection;